import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore, {
  FirebaseFirestoreTypes,
} from '@react-native-firebase/firestore';

import {CreditType, Fund, useReservationYears} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface CreditTypeEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  funds: Fund[];
  creditType?: CreditType;
}

export function CreditTypeEditor(props: CreditTypeEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();
  const reservationYears = useReservationYears();

  const [validating, setValidating] = useState(false);

  const collectionName = 'creditTypes';
  const saveFirestoreObject = useMutation(
    (firestoreObject: CreditType) => {
      if (props.creditType === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.creditType?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let creditType: {[key: string]: FormikStoreType} = {
    creditTypeId: Date.now(),
    fundId: undefined,
    allowAFO: false,
    allowCD: false,
    allowECC: false,
    allowFCC: false,
    allowReservation: false,
    creditType: undefined,
    // expirationYear: undefined,
    reservationYearId: undefined,
  };

  if (props.creditType !== undefined) {
    creditType.creditTypeId = props.creditType.creditTypeId;
    creditType.fundId = props.creditType.fundId;
    creditType.allowAFO = props.creditType.allowAFO;
    creditType.allowCD = props.creditType.allowCD;
    creditType.allowECC = props.creditType.allowECC;
    creditType.allowFCC = props.creditType.allowFCC;
    creditType.allowReservation = props.creditType.allowReservation;
    creditType.creditType = props.creditType.creditType;
    if (props.creditType.reservationYearId !== undefined) {
      creditType.reservationYearId = props.creditType.reservationYearId;
    }
    creditType.reservationYearId = props.creditType.reservationYearId;
    if (props.creditType.expirationYear !== undefined) {
      creditType.expirationYear = (
        props.creditType.expirationYear as FirebaseFirestoreTypes.Timestamp
      )?.toDate();
    }
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.creditType ? 'Edit' : 'Create'} Credit Type`,
              firestoreObject: creditType,
              fieldMetadata: [
                {
                  key: 'creditTypeId',
                  name: 'Credit Type ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Credit Type ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'creditType',
                  name: 'Credit Type',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Credit Type is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'expirationYear',
                  name: 'Expiration Date',
                  type: FormikStoreTypes.date,
                  required: false,
                  errorText: 'Select a date if type expires',
                  datesUTC: true,
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'fundId',
                  name: 'Fund',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText: 'Fund is a required field',
                  selectItems: {
                    documents: props.funds,
                    items: props.funds.map(fund => fund.ref),
                    labelExtractor: fund => fund?.fundCode,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'allowAFO',
                  name: 'Allowed for Fee Offset',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'allowCD',
                  name: 'Allowed for Capital Dividend',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'allowECC',
                  name: 'Allowed for External Credit Exchange',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'allowFCC',
                  name: 'Allowed for Internal Credit Exchange',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'allowReservation',
                  name: 'Allowed for Reservations',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'reservationYearId',
                  name: 'Reservation Year',
                  type: FormikStoreTypes.select,
                  required: false,
                  errorText: 'Please select a reservation year',
                  selectItems: {
                    documents: reservationYears,
                    items:
                      reservationYears?.map(
                        reservationYear => reservationYear.ref,
                      ) ?? [],
                    labelExtractor: reservationYear => reservationYear?.name,
                  },
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                console.log(
                  'saving with ' +
                    JSON.stringify(formObjects[0].firestoreObject, null, 2),
                );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as CreditType,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
